import {
  convertToDateFnsFormat,
  getTimeFormatString,
  getUserDateFormat,
  getUserTimeFormat,
} from "./formatDate";
import { format } from "date-fns";

/**
 * @typedef {Object} ApiErrorResponse
 * @property {string} error
 * @property {string=} code
 * @property {unknown=} details
 */

/**
 * @typedef {Object} ScheduleConflictDetails
 * @property {"SCHEDULE_CONFLICT"} type
 * @property {number} conflictingScheduleId
 * @property {string | null} conflictingScheduleName
 * @property {number} conflictingRouteId
 * @property {string | null} conflictingRouteName
 * @property {{ start: string | null, end: string | null }} overlapWindow
 * @property {{ id: number, name: string | null, vehicleNumber: string | null } | null} vehicle
 */

/**
 * @typedef {Object} VehicleScheduleConflictDetails
 * @property {"SCHEDULE_CONFLICT"} type
 * @property {number} vehicleId
 * @property {Array<{ scheduleId: number, scheduleName: string | null, routeId: number, routeName: string | null }>} conflictingSchedules
 * @property {{ start: string | null, end: string | null }} overlapWindow
 */

/**
 * @typedef {"RUNNING" | "SCHEDULED" | "COMPLETED"} ScheduleStatus
 */

const EMPTY_SCHEDULE_COUNTS = {
  total: 0,
  active: 0,
  scheduled: 0,
  running: 0,
  completed: 0,
};

const DEFAULT_SCHEDULE_CONFLICT_MESSAGE =
  "Schedule conflict detected. Please choose a different time or vehicle.";
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CLIENT_MONTHLY_LOOKAHEAD_MONTHS = 1200;

const toValidDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatWithUserPreferences = (value, { includeDate = true, includeTime = true } = {}) => {
  const date = toValidDate(value);

  if (!date) {
    return "";
  }

  const userDateFormat = convertToDateFnsFormat(getUserDateFormat());
  const userTimeFormat = getTimeFormatString(getUserTimeFormat());
  const parts = [];

  if (includeDate) {
    parts.push(userDateFormat);
  }

  if (includeTime) {
    parts.push(userTimeFormat);
  }

  return format(date, parts.join(" "));
};

const toNumberOrNull = (value) => {
  const normalizedValue = Number(value);
  return Number.isFinite(normalizedValue) ? normalizedValue : null;
};

const getIsoDatePortion = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return null;
  }

  if (normalizedValue.includes("T")) {
    return normalizedValue.split("T")[0] || null;
  }

  const dateMatch = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})/);
  return dateMatch ? dateMatch[1] : null;
};

const getDateParts = (value) => {
  const isoDate = getIsoDatePortion(value);

  if (!isoDate) {
    return null;
  }

  const [year, month, day] = isoDate.split("-").map(Number);

  if (![year, month, day].every(Number.isFinite)) {
    return null;
  }

  return { year, month, day };
};

const getEpochDay = (value) => {
  const dateParts = getDateParts(value);

  if (!dateParts) {
    return null;
  }

  return Math.floor(
    Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day) / MS_PER_DAY,
  );
};

const getDatePartsFromEpochDay = (epochDay) => {
  const date = new Date(epochDay * MS_PER_DAY);

  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
};

const getUtcWeekday = (epochDay) => new Date(epochDay * MS_PER_DAY).getUTCDay();

const getMonthIndexFromEpochDay = (epochDay) => {
  const { year, month } = getDatePartsFromEpochDay(epochDay);
  return year * 12 + (month - 1);
};

const getYearMonthFromIndex = (monthIndex) => {
  const normalizedMonthIndex = Number(monthIndex);
  const year = Math.floor(normalizedMonthIndex / 12);
  const month = normalizedMonthIndex - year * 12 + 1;

  return { year, month };
};

const getDaysInMonth = (year, month) =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

const buildLocalDateTimeIso = (epochDay, minutes) => {
  const { year, month, day } = getDatePartsFromEpochDay(epochDay);
  const date = new Date(
    year,
    month - 1,
    day,
    Math.floor(minutes / 60),
    minutes % 60,
    0,
    0,
  );

  return date.toISOString();
};

const getTimeInMinutes = (value) => {
  if (!value) {
    return null;
  }

  const normalizedValue = String(value).trim();

  if (/^\d{2}:\d{2}$/.test(normalizedValue)) {
    const [hours, minutes] = normalizedValue.split(":").map(Number);

    if ([hours, minutes].every(Number.isFinite)) {
      return hours * 60 + minutes;
    }
  }

  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.getHours() * 60 + parsedDate.getMinutes();
};

const normalizeEndCondition = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, "_");

  switch (normalizedValue) {
    case "BYDATE":
      return "BY_DATE";
    case "AFTEROCCURRENCES":
      return "AFTER_OCCURRENCES";
    case "NOEND":
      return "NO_END";
    default:
      return normalizedValue;
  }
};

const normalizeMonthlyType = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, "_");

  switch (normalizedValue) {
    case "DAYOFMONTH":
      return "DAY_OF_MONTH";
    case "DAYOFWEEKOFMONTH":
      return "DAY_OF_WEEK_OF_MONTH";
    default:
      return normalizedValue;
  }
};

const WEEKDAY_NAME_MAP = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

const normalizeWeekday = (value) => {
  if (value == null || value === "") {
    return null;
  }

  if (Number.isFinite(Number(value))) {
    const normalizedValue = Number(value);
    return normalizedValue >= 0 && normalizedValue <= 6
      ? normalizedValue
      : null;
  }

  const normalizedValue = String(value).trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(WEEKDAY_NAME_MAP, normalizedValue)
    ? WEEKDAY_NAME_MAP[normalizedValue]
    : null;
};

const getNormalizedWeeklyDays = (schedule) =>
  Array.from(
    new Set(
      (Array.isArray(schedule?.weeklyDays) ? schedule.weeklyDays : [])
        .map(normalizeWeekday)
        .filter((weekday) => weekday != null),
    ),
  ).sort((firstDay, secondDay) => firstDay - secondDay);

const getScheduleVehicleIds = (schedule) => {
  const directVehicleIds = Array.isArray(schedule?.vehicleIds)
    ? schedule.vehicleIds
    : [];
  const joinedVehicleIds = Array.isArray(schedule?.vehicleSchedule)
    ? schedule.vehicleSchedule.map(
        (vehicleSchedule) =>
          vehicleSchedule?.vehicleId ?? vehicleSchedule?.vehicle?.id,
      )
    : [];
  const summaryVehicleIds = Array.isArray(schedule?.vehicleScheduleSummary)
    ? schedule.vehicleScheduleSummary.map((summary) => summary?.vehicleId)
    : [];

  return Array.from(
    new Set(
      [...directVehicleIds, ...joinedVehicleIds, ...summaryVehicleIds]
        .map((vehicleId) => Number(vehicleId))
        .filter((vehicleId) => Number.isFinite(vehicleId) && vehicleId > 0),
    ),
  );
};

const getScheduleVehicleDetails = (schedule, vehicleId) => {
  const normalizedVehicleId = Number(vehicleId);

  if (!Number.isFinite(normalizedVehicleId) || normalizedVehicleId <= 0) {
    return null;
  }

  const joinedVehicle = Array.isArray(schedule?.vehicleSchedule)
    ? schedule.vehicleSchedule.find(
        (vehicleSchedule) =>
          Number(vehicleSchedule?.vehicleId ?? vehicleSchedule?.vehicle?.id) ===
          normalizedVehicleId,
      )?.vehicle
    : null;

  if (joinedVehicle) {
    return {
      id: normalizedVehicleId,
      name: joinedVehicle?.name || null,
      vehicleNumber:
        joinedVehicle?.vehicleNumber ||
        joinedVehicle?.VehicleNumber ||
        joinedVehicle?.vehicle_number ||
        null,
    };
  }

  const summaryVehicle = Array.isArray(schedule?.vehicleScheduleSummary)
    ? schedule.vehicleScheduleSummary.find(
        (summary) => Number(summary?.vehicleId) === normalizedVehicleId,
      )
    : null;

  if (summaryVehicle) {
    return {
      id: normalizedVehicleId,
      name: summaryVehicle?.vehicleName || null,
      vehicleNumber: summaryVehicle?.vehicleNumber || null,
    };
  }

  return {
    id: normalizedVehicleId,
    name: null,
    vehicleNumber: null,
  };
};

const getTimeWindow = (schedule) => {
  const start = getTimeInMinutes(schedule?.startTime);
  const end = getTimeInMinutes(schedule?.endTime);

  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null;
  }

  return { start, end };
};

const getSharedVehicleId = (firstVehicleIds = [], secondVehicleIds = []) => {
  const firstVehicleIdSet = new Set(firstVehicleIds);

  return (
    secondVehicleIds.find((vehicleId) => firstVehicleIdSet.has(vehicleId)) ||
    null
  );
};

const getScheduleRepetition = (schedule) =>
  String(schedule?.repitation ?? schedule?.repetition ?? "")
    .trim()
    .toUpperCase();

const getRepeatEveryValue = (schedule) => {
  const repeatEvery = Number(schedule?.repeatEvery);

  return Number.isFinite(repeatEvery) && repeatEvery > 0 ? repeatEvery : 1;
};

const getMaximumOccurrences = (schedule) => {
  if (normalizeEndCondition(schedule?.endCondition) !== "AFTER_OCCURRENCES") {
    return Number.POSITIVE_INFINITY;
  }

  const occurrences = Number(schedule?.occurrences);

  return Number.isFinite(occurrences) && occurrences > 0
    ? occurrences
    : Number.POSITIVE_INFINITY;
};

const getMonthlyOccurrenceDayForMonth = (schedule, year, month) => {
  const monthlyType = schedule.monthlyType;

  if (monthlyType === "DAY_OF_WEEK_OF_MONTH") {
    const monthlyWeekday =
      schedule.monthlyWeekDay != null
        ? schedule.monthlyWeekDay
        : getUtcWeekday(schedule.startDay);
    const monthlyWeekOrderMap = {
      FIRST: 1,
      SECOND: 2,
      THIRD: 3,
      FOURTH: 4,
      FIFTH: 5,
    };
    const monthlyWeekOrder =
      monthlyWeekOrderMap[schedule.monthlyWeekOrder] || 1;
    const firstDayOfMonth = Math.floor(
      Date.UTC(year, month - 1, 1) / MS_PER_DAY,
    );
    const firstWeekday = getUtcWeekday(firstDayOfMonth);
    const weekdayOffset = (monthlyWeekday - firstWeekday + 7) % 7;
    const dayOfMonth = 1 + weekdayOffset + (monthlyWeekOrder - 1) * 7;

    if (dayOfMonth > getDaysInMonth(year, month)) {
      return null;
    }

    return Math.floor(Date.UTC(year, month - 1, dayOfMonth) / MS_PER_DAY);
  }

  const fallbackDay = getDatePartsFromEpochDay(schedule.startDay).day;
  const dayOfMonth =
    Number.isFinite(Number(schedule.monthlyDay)) && Number(schedule.monthlyDay) > 0
      ? Number(schedule.monthlyDay)
      : fallbackDay;

  if (dayOfMonth > getDaysInMonth(year, month)) {
    return null;
  }

  return Math.floor(Date.UTC(year, month - 1, dayOfMonth) / MS_PER_DAY);
};

const getWeeklyOccurrenceIndex = (schedule, epochDay) => {
  const weeklyDays = schedule.weeklyDays;

  if (!weeklyDays.length) {
    return null;
  }

  const intervalWeeks = schedule.repeatEvery;
  const startWeekday = getUtcWeekday(schedule.startDay);
  const currentWeekday = getUtcWeekday(epochDay);
  const startWeekStart = schedule.startDay - startWeekday;
  const currentWeekStart = epochDay - currentWeekday;
  const weekDiff = (currentWeekStart - startWeekStart) / 7;
  const activeWeekIndex = Math.floor(weekDiff / intervalWeeks);
  const firstWeekDays = weeklyDays.filter(
    (weekday) => weekday >= startWeekday,
  );

  if (activeWeekIndex === 0) {
    return weeklyDays.filter(
      (weekday) => weekday >= startWeekday && weekday <= currentWeekday,
    ).length;
  }

  return (
    firstWeekDays.length +
    Math.max(0, activeWeekIndex - 1) * weeklyDays.length +
    weeklyDays.filter((weekday) => weekday <= currentWeekday).length
  );
};

const getMonthlyOccurrenceIndex = (schedule, epochDay) => {
  const currentMonthIndex = getMonthIndexFromEpochDay(epochDay);
  let occurrenceIndex = 0;

  for (
    let monthIndex = schedule.startMonthIndex;
    monthIndex <= currentMonthIndex;
    monthIndex += schedule.repeatEvery
  ) {
    const { year, month } = getYearMonthFromIndex(monthIndex);
    const occurrenceDay = getMonthlyOccurrenceDayForMonth(
      schedule,
      year,
      month,
    );

    if (
      occurrenceDay == null ||
      occurrenceDay < schedule.startDay ||
      occurrenceDay > epochDay
    ) {
      continue;
    }

    occurrenceIndex += 1;

    if (occurrenceDay === epochDay) {
      return occurrenceIndex;
    }
  }

  return null;
};

const getOccurrenceIndexForDay = (schedule, epochDay) => {
  switch (schedule.repitation) {
    case "ONETIME":
      return epochDay === schedule.startDay ? 1 : null;
    case "DAILY":
      return Math.floor((epochDay - schedule.startDay) / schedule.repeatEvery) + 1;
    case "WEEKLY":
      return getWeeklyOccurrenceIndex(schedule, epochDay);
    case "MONTHLY":
      return getMonthlyOccurrenceIndex(schedule, epochDay);
    default:
      return null;
  }
};

const isOccurrenceDay = (schedule, epochDay) => {
  if (
    schedule.startDay == null ||
    epochDay < schedule.startDay ||
    !Number.isFinite(epochDay)
  ) {
    return false;
  }

  if (
    Number.isFinite(schedule.byDateEndDay) &&
    epochDay > schedule.byDateEndDay
  ) {
    return false;
  }

  let occursOnDay = false;

  switch (schedule.repitation) {
    case "ONETIME":
      occursOnDay = epochDay === schedule.startDay;
      break;
    case "DAILY":
      occursOnDay =
        (epochDay - schedule.startDay) % schedule.repeatEvery === 0;
      break;
    case "WEEKLY": {
      const weeklyDays = schedule.weeklyDays;

      if (!weeklyDays.length) {
        return false;
      }

      const currentWeekday = getUtcWeekday(epochDay);
      const startWeekday = getUtcWeekday(schedule.startDay);
      const startWeekStart = schedule.startDay - startWeekday;
      const currentWeekStart = epochDay - currentWeekday;
      const weekDiff = (currentWeekStart - startWeekStart) / 7;

      occursOnDay =
        weekDiff >= 0 &&
        Number.isInteger(weekDiff) &&
        weekDiff % schedule.repeatEvery === 0 &&
        weeklyDays.includes(currentWeekday);
      break;
    }
    case "MONTHLY": {
      const currentMonthIndex = getMonthIndexFromEpochDay(epochDay);
      const monthDiff = currentMonthIndex - schedule.startMonthIndex;

      if (monthDiff < 0 || monthDiff % schedule.repeatEvery !== 0) {
        return false;
      }

      const { year, month } = getDatePartsFromEpochDay(epochDay);
      const monthlyOccurrenceDay = getMonthlyOccurrenceDayForMonth(
        schedule,
        year,
        month,
      );

      occursOnDay = monthlyOccurrenceDay === epochDay;
      break;
    }
    default:
      return false;
  }

  if (!occursOnDay) {
    return false;
  }

  if (!Number.isFinite(schedule.maximumOccurrences)) {
    return true;
  }

  const occurrenceIndex = getOccurrenceIndexForDay(schedule, epochDay);
  return occurrenceIndex != null && occurrenceIndex <= schedule.maximumOccurrences;
};

const getLastOccurrenceDay = (schedule) => {
  if (schedule.startDay == null) {
    return null;
  }

  if (schedule.repitation === "ONETIME") {
    return schedule.startDay;
  }

  if (Number.isFinite(schedule.byDateEndDay)) {
    return schedule.byDateEndDay;
  }

  if (!Number.isFinite(schedule.maximumOccurrences)) {
    return Number.POSITIVE_INFINITY;
  }

  if (schedule.maximumOccurrences <= 0) {
    return schedule.startDay;
  }

  switch (schedule.repitation) {
    case "DAILY":
      return (
        schedule.startDay +
        (schedule.maximumOccurrences - 1) * schedule.repeatEvery
      );
    case "WEEKLY": {
      const weeklyDays = schedule.weeklyDays;

      if (!weeklyDays.length) {
        return schedule.startDay;
      }

      const startWeekday = getUtcWeekday(schedule.startDay);
      const startWeekStart = schedule.startDay - startWeekday;
      const firstWeekDays = weeklyDays.filter(
        (weekday) => weekday >= startWeekday,
      );

      if (schedule.maximumOccurrences <= firstWeekDays.length) {
        return (
          startWeekStart + firstWeekDays[schedule.maximumOccurrences - 1]
        );
      }

      const remainingOccurrences =
        schedule.maximumOccurrences - firstWeekDays.length;
      const lastCycleIndex = Math.floor(
        (remainingOccurrences - 1) / weeklyDays.length,
      );
      const lastWeekday =
        weeklyDays[(remainingOccurrences - 1) % weeklyDays.length];

      return (
        startWeekStart +
        (lastCycleIndex + 1) * schedule.repeatEvery * 7 +
        lastWeekday
      );
    }
    case "MONTHLY": {
      let occurrenceCount = 0;

      for (
        let monthIndex = schedule.startMonthIndex;
        occurrenceCount < schedule.maximumOccurrences;
        monthIndex += schedule.repeatEvery
      ) {
        const { year, month } = getYearMonthFromIndex(monthIndex);
        const occurrenceDay = getMonthlyOccurrenceDayForMonth(
          schedule,
          year,
          month,
        );

        if (occurrenceDay == null || occurrenceDay < schedule.startDay) {
          continue;
        }

        occurrenceCount += 1;

        if (occurrenceCount === schedule.maximumOccurrences) {
          return occurrenceDay;
        }
      }

      return schedule.startDay;
    }
    default:
      return schedule.startDay;
  }
};

const gcd = (firstValue, secondValue) => {
  let a = Math.abs(firstValue);
  let b = Math.abs(secondValue);

  while (b !== 0) {
    const remainder = a % b;
    a = b;
    b = remainder;
  }

  return a || 1;
};

const lcm = (firstValue, secondValue) =>
  Math.abs(firstValue * secondValue) / gcd(firstValue, secondValue);

const getNonMonthlyCycleLength = (firstSchedule, secondSchedule) => {
  const getPeriodLength = (schedule) => {
    switch (schedule.repitation) {
      case "DAILY":
        return schedule.repeatEvery;
      case "WEEKLY":
        return schedule.repeatEvery * 7;
      default:
        return 1;
    }
  };

  return lcm(getPeriodLength(firstSchedule), getPeriodLength(secondSchedule));
};

const getOpenEndedMonthlySearchEndDay = (rangeStartDay) => {
  const rangeStartMonthIndex = getMonthIndexFromEpochDay(rangeStartDay);
  const rangeEndMonthIndex =
    rangeStartMonthIndex + CLIENT_MONTHLY_LOOKAHEAD_MONTHS - 1;
  const { year, month } = getYearMonthFromIndex(rangeEndMonthIndex);
  const lastDayOfMonth = getDaysInMonth(year, month);

  return Math.floor(Date.UTC(year, month - 1, lastDayOfMonth) / MS_PER_DAY);
};

const buildNormalizedSchedule = (schedule) => {
  const startDay = getEpochDay(schedule?.startDate || schedule?.startTime);
  const repitation = getScheduleRepetition(schedule);
  const byDateEndDay =
    normalizeEndCondition(schedule?.endCondition) === "BY_DATE"
      ? getEpochDay(schedule?.endDate)
      : null;
  const timeWindow = getTimeWindow(schedule);

  return {
    id: toNumberOrNull(schedule?.id),
    name: schedule?.name || null,
    routeId: toNumberOrNull(schedule?.routeId ?? schedule?.route?.id),
    routeName: schedule?.route?.name || schedule?.routeName || null,
    repitation,
    repeatEvery: getRepeatEveryValue(schedule),
    startDay,
    startMonthIndex:
      startDay == null ? null : getMonthIndexFromEpochDay(startDay),
    byDateEndDay,
    maximumOccurrences: getMaximumOccurrences(schedule),
    timeWindow,
    weeklyDays: getNormalizedWeeklyDays(schedule),
    monthlyType: normalizeMonthlyType(schedule?.monthlyType || "DAY_OF_MONTH"),
    monthlyDay: toNumberOrNull(schedule?.monthlyDay),
    monthlyWeekOrder: String(schedule?.monthlyWeekOrder || "FIRST")
      .trim()
      .toUpperCase(),
    monthlyWeekDay: normalizeWeekday(schedule?.monthlyWeekDay),
    vehicleIds: getScheduleVehicleIds(schedule),
    originalSchedule: schedule,
  };
};

/**
 * Expands a schedule definition into planned trip occurrences for a bounded
 * date range. This intentionally returns planned occurrences; actual trip
 * status/progress must still come from a trip execution API.
 */
export const expandScheduleOccurrences = (
  schedule,
  { rangeStart = new Date(), rangeEnd = new Date() } = {},
) => {
  const normalizedSchedule = buildNormalizedSchedule(schedule);
  const firstDay = getEpochDay(rangeStart);
  const lastDay = getEpochDay(rangeEnd);

  if (
    normalizedSchedule.startDay == null ||
    normalizedSchedule.timeWindow == null ||
    firstDay == null ||
    lastDay == null ||
    firstDay > lastDay
  ) {
    return [];
  }

  const occurrences = [];
  const boundedStartDay = Math.max(firstDay, normalizedSchedule.startDay);

  for (let epochDay = boundedStartDay; epochDay <= lastDay; epochDay += 1) {
    if (!isOccurrenceDay(normalizedSchedule, epochDay)) {
      continue;
    }

    occurrences.push({
      scheduleId: normalizedSchedule.id,
      occurrenceIndex: getOccurrenceIndexForDay(normalizedSchedule, epochDay),
      start: buildLocalDateTimeIso(
        epochDay,
        normalizedSchedule.timeWindow.start,
      ),
      end: buildLocalDateTimeIso(
        epochDay,
        normalizedSchedule.timeWindow.end,
      ),
    });
  }

  return occurrences;
};

const buildOverlapWindow = (epochDay, firstSchedule, secondSchedule) => {
  const overlapStart = Math.max(
    firstSchedule.timeWindow.start,
    secondSchedule.timeWindow.start,
  );
  const overlapEnd = Math.min(
    firstSchedule.timeWindow.end,
    secondSchedule.timeWindow.end,
  );

  if (overlapEnd <= overlapStart) {
    return null;
  }

  return {
    start: buildLocalDateTimeIso(epochDay, overlapStart),
    end: buildLocalDateTimeIso(epochDay, overlapEnd),
  };
};

const findConflictDayWithMonthlySchedule = (
  monthlySchedule,
  otherSchedule,
  rangeStartDay,
  rangeEndDay,
) => {
  if (
    monthlySchedule.startMonthIndex == null ||
    rangeStartDay > rangeEndDay
  ) {
    return null;
  }

  const rangeStartMonthIndex = getMonthIndexFromEpochDay(rangeStartDay);
  const rangeEndMonthIndex = getMonthIndexFromEpochDay(rangeEndDay);
  const skippedMonthCount = Math.max(
    0,
    Math.floor(
      (rangeStartMonthIndex - monthlySchedule.startMonthIndex) /
        monthlySchedule.repeatEvery,
    ),
  );
  let monthIndex =
    monthlySchedule.startMonthIndex +
    skippedMonthCount * monthlySchedule.repeatEvery;

  for (
    ;
    monthIndex <= rangeEndMonthIndex;
    monthIndex += monthlySchedule.repeatEvery
  ) {
    const { year, month } = getYearMonthFromIndex(monthIndex);
    const occurrenceDay = getMonthlyOccurrenceDayForMonth(
      monthlySchedule,
      year,
      month,
    );

    if (
      occurrenceDay == null ||
      occurrenceDay < rangeStartDay ||
      occurrenceDay > rangeEndDay ||
      !isOccurrenceDay(monthlySchedule, occurrenceDay)
    ) {
      continue;
    }

    if (isOccurrenceDay(otherSchedule, occurrenceDay)) {
      return occurrenceDay;
    }
  }

  return null;
};

const findFirstConflictDay = (firstSchedule, secondSchedule) => {
  const firstScheduleEndDay = getLastOccurrenceDay(firstSchedule);
  const secondScheduleEndDay = getLastOccurrenceDay(secondSchedule);
  const rangeStartDay = Math.max(firstSchedule.startDay, secondSchedule.startDay);
  const finiteEndDays = [firstScheduleEndDay, secondScheduleEndDay].filter(
    (endDay) => Number.isFinite(endDay),
  );

  if (
    finiteEndDays.length > 0 &&
    Math.min(...finiteEndDays) < rangeStartDay
  ) {
    return null;
  }

  if (firstSchedule.repitation === "ONETIME") {
    return isOccurrenceDay(secondSchedule, firstSchedule.startDay)
      ? firstSchedule.startDay
      : null;
  }

  if (secondSchedule.repitation === "ONETIME") {
    return isOccurrenceDay(firstSchedule, secondSchedule.startDay)
      ? secondSchedule.startDay
      : null;
  }

  if (
    firstSchedule.repitation === "MONTHLY" ||
    secondSchedule.repitation === "MONTHLY"
  ) {
    const rangeEndDay =
      finiteEndDays.length > 0
        ? Math.min(...finiteEndDays)
        : getOpenEndedMonthlySearchEndDay(rangeStartDay);

    if (rangeEndDay < rangeStartDay) {
      return null;
    }

    return firstSchedule.repitation === "MONTHLY"
      ? findConflictDayWithMonthlySchedule(
          firstSchedule,
          secondSchedule,
          rangeStartDay,
          rangeEndDay,
        )
      : findConflictDayWithMonthlySchedule(
          secondSchedule,
          firstSchedule,
          rangeStartDay,
          rangeEndDay,
        );
  }

  const cycleLength = getNonMonthlyCycleLength(firstSchedule, secondSchedule);
  const rangeEndDay =
    finiteEndDays.length > 0
      ? Math.min(...finiteEndDays, rangeStartDay + cycleLength - 1)
      : rangeStartDay + cycleLength - 1;

  for (let epochDay = rangeStartDay; epochDay <= rangeEndDay; epochDay += 1) {
    if (
      isOccurrenceDay(firstSchedule, epochDay) &&
      isOccurrenceDay(secondSchedule, epochDay)
    ) {
      return epochDay;
    }
  }

  return null;
};

const getNamedEntityLabel = (preferredName, fallbackPrefix) => {
  if (preferredName && String(preferredName).trim().length > 0) {
    return String(preferredName).trim();
  }

  return fallbackPrefix;
};

const getVehicleLabel = (vehicle) => {
  if (!vehicle) {
    return "The selected vehicle";
  }

  const parts = [vehicle.name, vehicle.vehicleNumber]
    .map((value) => (value == null ? "" : String(value).trim()))
    .filter(Boolean);

  if (parts.length > 0) {
    return parts.join(" · ");
  }

  return "The selected vehicle";
};

const formatScheduleConflictDetails = (
  details,
  fallbackMessage = DEFAULT_SCHEDULE_CONFLICT_MESSAGE,
) => {
  if (!details || details.type !== "SCHEDULE_CONFLICT") {
    return fallbackMessage;
  }

  const vehicleLabel = getVehicleLabel(details.vehicle);
  const routeLabel = getNamedEntityLabel(details.conflictingRouteName, "Route");
  const scheduleLabel = getNamedEntityLabel(
    details.conflictingScheduleName,
    "Schedule",
  );
  const overlapLabel = formatScheduleWindow(details.overlapWindow);

  return `${vehicleLabel} is already assigned to ${routeLabel} in ${scheduleLabel}${overlapLabel ? ` during ${overlapLabel}` : ""}.`;
};

export const getApiErrorResponse = (error) => {
  const errorResponse = error?.response?.data;

  if (errorResponse && typeof errorResponse === "object") {
    return {
      error: errorResponse.error || error.message || "Request failed",
      code: errorResponse.code,
      details: errorResponse.details,
    };
  }

  return {
    error: error?.message || "Request failed",
  };
};

export const getGenericApiErrorMessage = (
  error,
  fallbackMessage = "Something went wrong. Please try again.",
) => {
  const apiError = getApiErrorResponse(error);
  return apiError.error || fallbackMessage;
};

export const formatScheduleDateTime = (value, options = {}) =>
  formatWithUserPreferences(value, options);

export const formatScheduleWindow = (window) => {
  const start = toValidDate(window?.start);
  const end = toValidDate(window?.end);

  if (start && end) {
    const sameDay = start.toDateString() === end.toDateString();

    if (sameDay) {
      return `${formatScheduleDateTime(start)} - ${formatScheduleDateTime(end, {
        includeDate: false,
      })}`;
    }

    return `${formatScheduleDateTime(start)} - ${formatScheduleDateTime(end)}`;
  }

  if (start) {
    return `Starts ${formatScheduleDateTime(start)}`;
  }

  if (end) {
    return `Until ${formatScheduleDateTime(end)}`;
  }

  return "";
};

export const getScheduleStatusMeta = (status) => {
  switch (status) {
    case "RUNNING":
      return {
        label: "Running",
        backgroundColor: "#E9F9EF",
        textColor: "#1B7F3A",
        borderColor: "#86D7A1",
      };
    case "SCHEDULED":
      return {
        label: "Scheduled",
        backgroundColor: "#EEF5FF",
        textColor: "#1E5BB8",
        borderColor: "#F5A623",
      };
    case "COMPLETED":
      return {
        label: "Completed",
        backgroundColor: "#F2F4F7",
        textColor: "#667085",
        borderColor: "#D0D5DD",
      };
    default:
      return {
        label: status || "Unknown",
        backgroundColor: "#F5F5F5",
        textColor: "#414958",
        borderColor: "#D0D5DD",
      };
  }
};

export const getScheduleOccurrenceDetails = (schedule) => {
  switch (schedule?.scheduleStatus) {
    case "RUNNING": {
      const windowText = formatScheduleWindow(schedule.currentOccurrence);
      return {
        label: "Running now",
        text: windowText || "Running now",
        sortValue:
          schedule?.currentOccurrence?.start ||
          schedule?.currentOccurrence?.end ||
          schedule?.startTime ||
          null,
      };
    }
    case "SCHEDULED": {
      const windowText = formatScheduleWindow(schedule.nextOccurrence);
      return {
        label: "Next run",
        text: windowText ? `Next run: ${windowText}` : "Next run",
        sortValue:
          schedule?.nextOccurrence?.start || schedule?.startTime || null,
      };
    }
    case "COMPLETED":
      return {
        label: "Completed",
        text: "Completed",
        sortValue:
          schedule?.currentOccurrence?.end ||
          schedule?.nextOccurrence?.end ||
          schedule?.endTime ||
          null,
      };
    default: {
      const fallbackWindow =
        formatScheduleWindow(schedule?.nextOccurrence) ||
        formatScheduleWindow(schedule?.currentOccurrence) ||
        formatScheduleWindow({
          start: schedule?.startTime,
          end: schedule?.endTime,
        });

      return {
        label: "Schedule window",
        text: fallbackWindow || "Schedule window unavailable",
        sortValue:
          schedule?.nextOccurrence?.start ||
          schedule?.currentOccurrence?.start ||
          schedule?.startTime ||
          null,
      };
    }
  }
};

export const buildScheduleConflictMessage = (
  error,
  fallbackMessage = DEFAULT_SCHEDULE_CONFLICT_MESSAGE,
) => {
  const apiError = getApiErrorResponse(error);

  if (apiError.code !== "SCHEDULE_CONFLICT") {
    return apiError.error || fallbackMessage;
  }

  /** @type {ScheduleConflictDetails | undefined} */
  const details = apiError.details;

  if (!details || details.type !== "SCHEDULE_CONFLICT") {
    return apiError.error || fallbackMessage;
  }

  return formatScheduleConflictDetails(details, apiError.error || fallbackMessage);
};

export const buildLocalScheduleConflictMessage = (
  details,
  fallbackMessage = DEFAULT_SCHEDULE_CONFLICT_MESSAGE,
) => formatScheduleConflictDetails(details, fallbackMessage);

export const buildVehicleScheduleConflictMessage = (
  error,
  {
    vehicleName = null,
    vehicleNumber = null,
    fallbackMessage = DEFAULT_SCHEDULE_CONFLICT_MESSAGE,
  } = {},
) => {
  const apiError = getApiErrorResponse(error);

  if (apiError.code !== "SCHEDULE_CONFLICT") {
    return apiError.error || fallbackMessage;
  }

  /** @type {VehicleScheduleConflictDetails | undefined} */
  const details = apiError.details;

  if (!details || details.type !== "SCHEDULE_CONFLICT") {
    return apiError.error || fallbackMessage;
  }

  const vehicleLabel = getVehicleLabel(
    {
      name: vehicleName,
      vehicleNumber,
    },
    details.vehicleId,
  );

  const overlapLabel = formatScheduleWindow(details.overlapWindow);
  const conflicts = Array.isArray(details.conflictingSchedules)
    ? details.conflictingSchedules
    : [];

  if (conflicts.length === 0) {
    return `${vehicleLabel} already has another schedule in the selected time window${overlapLabel ? ` (${overlapLabel})` : ""}.`;
  }

  const formattedConflicts = conflicts
    .slice(0, 3)
    .map((conflict) => {
      const routeLabel = getNamedEntityLabel(
        conflict.routeName,
        "Route",
        conflict.routeId,
      );
      const scheduleLabel = getNamedEntityLabel(
        conflict.scheduleName,
        "Schedule",
        conflict.scheduleId,
      );

      return `${routeLabel} / ${scheduleLabel}`;
    })
    .join("; ");

  const extraConflictCount =
    conflicts.length > 3 ? ` and ${conflicts.length - 3} more` : "";

  return `${vehicleLabel} already has conflicting schedules${overlapLabel ? ` during ${overlapLabel}` : ""}: ${formattedConflicts}${extraConflictCount}.`;
};

export const findScheduleConflictFromList = (
  candidateSchedule,
  existingSchedules = [],
  { ignoreScheduleId = null } = {},
) => {
  const normalizedCandidateSchedule = buildNormalizedSchedule(candidateSchedule);

  if (
    normalizedCandidateSchedule.startDay == null ||
    !normalizedCandidateSchedule.timeWindow ||
    normalizedCandidateSchedule.vehicleIds.length === 0
  ) {
    return null;
  }

  const normalizedIgnoreScheduleId = toNumberOrNull(ignoreScheduleId);
  let earliestConflict = null;

  (Array.isArray(existingSchedules) ? existingSchedules : []).forEach(
    (existingSchedule) => {
      const normalizedExistingSchedule = buildNormalizedSchedule(existingSchedule);

      if (
        normalizedExistingSchedule.startDay == null ||
        !normalizedExistingSchedule.timeWindow ||
        (normalizedIgnoreScheduleId != null &&
          normalizedExistingSchedule.id === normalizedIgnoreScheduleId)
      ) {
        return;
      }

      const sharedVehicleId = getSharedVehicleId(
        normalizedCandidateSchedule.vehicleIds,
        normalizedExistingSchedule.vehicleIds,
      );

      if (!sharedVehicleId) {
        return;
      }

      const overlapWindow = buildOverlapWindow(
        normalizedCandidateSchedule.startDay,
        normalizedCandidateSchedule,
        normalizedExistingSchedule,
      );

      if (!overlapWindow) {
        return;
      }

      const conflictDay = findFirstConflictDay(
        normalizedCandidateSchedule,
        normalizedExistingSchedule,
      );

      if (conflictDay == null) {
        return;
      }

      const conflictOverlapWindow = buildOverlapWindow(
        conflictDay,
        normalizedCandidateSchedule,
        normalizedExistingSchedule,
      );

      if (!conflictOverlapWindow) {
        return;
      }

      const candidateConflict = {
        type: "SCHEDULE_CONFLICT",
        conflictingScheduleId: normalizedExistingSchedule.id,
        conflictingScheduleName: normalizedExistingSchedule.name,
        conflictingRouteId: normalizedExistingSchedule.routeId,
        conflictingRouteName: normalizedExistingSchedule.routeName,
        overlapWindow: conflictOverlapWindow,
        vehicle:
          getScheduleVehicleDetails(existingSchedule, sharedVehicleId) || null,
        sortDay: conflictDay,
        sortStart: conflictOverlapWindow.start,
      };

      if (
        !earliestConflict ||
        candidateConflict.sortDay < earliestConflict.sortDay ||
        (candidateConflict.sortDay === earliestConflict.sortDay &&
          String(candidateConflict.sortStart) <
            String(earliestConflict.sortStart))
      ) {
        earliestConflict = candidateConflict;
      }
    },
  );

  if (!earliestConflict) {
    return null;
  }

  const { sortDay, sortStart, ...conflictDetails } = earliestConflict;
  return conflictDetails;
};

export const getScheduleCounts = (summary) => {
  const running = Number(summary?.scheduleCounts?.running || 0);
  const scheduled = Number(summary?.scheduleCounts?.scheduled || 0);

  return {
    ...EMPTY_SCHEDULE_COUNTS,
    ...(summary?.scheduleCounts || {}),
    active: Number(
      summary?.scheduleCounts?.active != null
        ? summary.scheduleCounts.active
        : running + scheduled,
    ),
    scheduled,
    running,
    completed: Number(summary?.scheduleCounts?.completed || 0),
    total: Number(summary?.scheduleCounts?.total || 0),
  };
};

export const getScheduleVehicleNames = (schedule) => {
  const summaryNames = Array.isArray(schedule?.vehicleScheduleSummary)
    ? schedule.vehicleScheduleSummary
        .map((summary) =>
          [summary?.vehicleName, summary?.vehicleNumber]
            .map((value) => (value == null ? "" : String(value).trim()))
            .filter(Boolean)
            .join(" · "),
        )
        .filter(Boolean)
    : [];

  if (summaryNames.length > 0) {
    return summaryNames.join(", ");
  }

  const assignedVehicleNames = Array.isArray(schedule?.vehicleSchedule)
    ? schedule.vehicleSchedule
        .map((vehicleSchedule) =>
          [
            vehicleSchedule?.vehicle?.name,
            vehicleSchedule?.vehicle?.vehicleNumber,
            vehicleSchedule?.vehicle?.VehicleNumber,
          ]
            .map((value) => (value == null ? "" : String(value).trim()))
            .filter(Boolean)
            .join(" · "),
        )
        .filter(Boolean)
    : [];

  return assignedVehicleNames.length > 0 ? assignedVehicleNames.join(", ") : "N/A";
};

export const buildVehicleScheduleSummaryMap = (schedules = []) => {
  const summaries = new Map();

  schedules.forEach((schedule) => {
    const vehicleSummaries = Array.isArray(schedule?.vehicleScheduleSummary)
      ? schedule.vehicleScheduleSummary
      : [];

    vehicleSummaries.forEach((summary) => {
      if (!summary?.vehicleId) {
        return;
      }

      const existingSummary = summaries.get(summary.vehicleId);

      if (!existingSummary) {
        summaries.set(summary.vehicleId, {
          ...summary,
          scheduleCounts: getScheduleCounts(summary),
        });
        return;
      }

      const existingName = existingSummary.vehicleName || existingSummary.vehicleNumber;
      const nextName = summary.vehicleName || summary.vehicleNumber;

      summaries.set(summary.vehicleId, {
        ...existingSummary,
        ...(!existingName && nextName ? summary : {}),
        scheduleCounts: getScheduleCounts(summary),
      });
    });
  });

  return summaries;
};

export const getVehicleScheduleSummaries = (schedules = []) =>
  Array.from(buildVehicleScheduleSummaryMap(schedules).values());

export const getAggregatedVehicleScheduleCounts = (schedules = []) =>
  getVehicleScheduleSummaries(schedules).reduce(
    (accumulator, summary) => {
      const counts = getScheduleCounts(summary);

      return {
        total: accumulator.total + counts.total,
        active: accumulator.active + counts.active,
        scheduled: accumulator.scheduled + counts.scheduled,
        running: accumulator.running + counts.running,
        completed: accumulator.completed + counts.completed,
      };
    },
    { ...EMPTY_SCHEDULE_COUNTS },
  );
